/**
 * MedicalInfoPrompt
 *
 * Member-portal nudge: for each of the family's athletes with no medical
 * information on file, "add it, or tell us there is nothing to report."
 *
 * The server decides who is listed (GET /medical-info/mine: the caller's own
 * active athletes with blank medical fields and no "nothing to report"
 * confirmation). Either answer takes the athlete off that list for good, so
 * the prompt stops on every device, not just this browser.
 *
 * Renders nothing when:
 *   - the signed-in person is staff or an admin (the nudge belongs to the family);
 *   - the list has not loaded, or failed to load (unknown is not "missing");
 *   - no athlete is missing medical information;
 *   - the member chose "Later" in the last 30 days.
 *
 * "Later" is a per-browser convenience (localStorage), keyed by membership.
 * It never blocks anything: the prompt is a card on the dashboard, not a
 * dialog, and if storage is unavailable it simply shows again next visit.
 *
 * What a parent types here is medical information: it is sent once to the
 * server and never logged, stored in the browser, or shown back.
 */

import React, { useEffect, useState } from "react";
import { HeartPulse, X } from "lucide-react";
import { Button, Card, Textarea } from "../ui";
import { storedRoleIsAdminOrStaff } from "../../utils/roles";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import {
  answerMedicalInfo,
  getMyAthletesMissingMedicalInfo,
  medicalInfoErrorMessage,
  MyAthleteMissingMedicalInfo,
} from "../../api/medicalInfoAPI";

export const MEDICAL_INFO_PROMPT_SNOOZE_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

const storageKey = (membershipId: string) => `medicalInfoPromptDismissedAt:${membershipId}`;

/** True while a "Later" is still inside the snooze window. */
export const isMedicalInfoPromptSnoozed = (membershipId: string, now: number = Date.now()): boolean => {
  try {
    const raw = localStorage.getItem(storageKey(membershipId));
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return now - at < MEDICAL_INFO_PROMPT_SNOOZE_DAYS * DAY_MS;
  } catch {
    return false;
  }
};

interface Props {
  membership: { id?: string } | null | undefined;
}

const MedicalInfoPrompt: React.FC<Props> = ({ membership }) => {
  const { isDarkMode } = useTheme();
  const membershipId = membership?.id ? String(membership.id) : "";
  const isStaff = storedRoleIsAdminOrStaff();

  const [dismissed, setDismissed] = useState(false);
  // null = not known yet (still loading, or the load failed).
  const [athletes, setAthletes] = useState<MyAthleteMissingMedicalInfo[] | null>(null);
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [medicalInformation, setMedicalInformation] = useState("");
  const [medication, setMedication] = useState("");
  const [savingFor, setSavingFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const snoozed = membershipId ? isMedicalInfoPromptSnoozed(membershipId) : false;
  const shouldAsk = Boolean(membershipId) && !isStaff && !snoozed && !dismissed;

  useEffect(() => {
    if (!shouldAsk) return;
    let cancelled = false;
    const token = localStorage.getItem("authToken") || "";
    if (!token) return;
    getMyAthletesMissingMedicalInfo(token)
      .then((list) => {
        if (!cancelled) setAthletes(list);
      })
      .catch(() => {
        // A nudge is never worth an error on the dashboard: stay hidden.
        if (!cancelled) setAthletes(null);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldAsk, membershipId]);

  if (!shouldAsk) return null;
  if (!athletes || athletes.length === 0) return null;

  const later = () => {
    try {
      localStorage.setItem(storageKey(membershipId), String(Date.now()));
    } catch {
      /* private mode / quota — the prompt just shows again next visit */
    }
    setDismissed(true);
  };

  const closeForm = () => {
    setOpenFor(null);
    setMedicalInformation("");
    setMedication("");
  };

  const answer = async (athleteId: string, body: Parameters<typeof answerMedicalInfo>[2]) => {
    setSavingFor(athleteId);
    setError(null);
    try {
      await answerMedicalInfo(localStorage.getItem("authToken") || "", athleteId, body);
      setAthletes((list) => (list || []).filter((a) => a.athlete_id !== athleteId));
      closeForm();
    } catch (err) {
      setError(medicalInfoErrorMessage(err, "Could not save. Please try again."));
    } finally {
      setSavingFor(null);
    }
  };

  const hasText = medicalInformation.trim().length > 0 || medication.trim().length > 0;
  const primary = getPrimaryText(isDarkMode);
  const muted = getSecondaryText(isDarkMode);

  return (
    <Card padding="md" className="mb-4 sm:mb-6" role="status" aria-label="Medical information">
      <div className="flex items-start gap-3">
        <HeartPulse className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${primary}`}>Do we have the medical information we need?</p>
          <p className={`text-sm mt-1 ${muted}`}>
            Tell us about any allergies, conditions or medication our coaches should know about. If there is nothing,
            choose &quot;Nothing to report&quot; and we will stop asking.
          </p>

          {error && (
            <p role="alert" className="text-sm mt-2 text-red-600">
              {error}
            </p>
          )}

          <ul className="mt-3 space-y-3">
            {athletes.map((athlete) => {
              const name = [athlete.first_name, athlete.last_name].filter(Boolean).join(" ") || "Your athlete";
              const busy = savingFor === athlete.athlete_id;
              const open = openFor === athlete.athlete_id;
              return (
                <li key={athlete.athlete_id} aria-label={name}>
                  <p className={`text-sm font-medium ${primary}`}>{name}</p>
                  {open ? (
                    <div className="mt-2 space-y-3">
                      <Textarea
                        label="Allergies or medical conditions"
                        size="md16"
                        rows={3}
                        maxLength={5000}
                        value={medicalInformation}
                        onChange={(e) => setMedicalInformation(e.target.value)}
                      />
                      <Textarea
                        label="Medication"
                        size="md16"
                        rows={2}
                        maxLength={5000}
                        value={medication}
                        onChange={(e) => setMedication(e.target.value)}
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="save"
                          size="md"
                          disabled={busy || !hasText}
                          onClick={() =>
                            answer(athlete.athlete_id, {
                              medical_information: medicalInformation,
                              medication,
                            })
                          }
                        >
                          {busy ? "Saving…" : "Save"}
                        </Button>
                        <Button variant="secondary" size="md" disabled={busy} onClick={closeForm}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        variant="primary"
                        size="md"
                        disabled={busy}
                        onClick={() => {
                          setError(null);
                          setMedicalInformation("");
                          setMedication("");
                          setOpenFor(athlete.athlete_id);
                        }}
                      >
                        Add medical information
                      </Button>
                      <Button
                        variant="secondary"
                        size="md"
                        disabled={busy}
                        onClick={() => answer(athlete.athlete_id, { nothing_to_report: true })}
                      >
                        {busy ? "Saving…" : "Nothing to report"}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-4">
            <Button variant="ghost" size="md" onClick={later}>
              Later
            </Button>
          </div>
        </div>
        <Button
          type="button"
          variant="neutral"
          size="xs"
          iconOnly
          onClick={later}
          aria-label="Dismiss"
          className="flex-shrink-0"
          icon={<X className="w-4 h-4" aria-hidden="true" />}
        />
      </div>
    </Card>
  );
};

export default MedicalInfoPrompt;
